import React, { useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { Card, CardBody } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';

import { getOrderTrend, getOrderTrendSites } from './orderTrendApi';
import { dashboardPalette } from './chartTheme';

const METRICS = [
  { value: 'orders', label: 'Orders' },
  { value: 'revenue', label: 'Revenue' },
];
const LOOKBACK_WEEKS = [2, 4, 6, 8, 10, 26];
const VIEW_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
];
const CHART_TYPES = [
  { value: 'line', label: 'Line' },
  { value: 'columns', label: 'Columns' },
  { value: 'modulation', label: 'Variance' },
];
const HOT_PINK = '#ff3f8e';
const HOT_ORANGE = '#ff8248';
const HOT_YELLOW = '#ffd35a';
const VARIANCE_POSITIVE_COLOR = '#00ff88';
const VARIANCE_NEUTRAL_COLOR = '#ff9500';
const VARIANCE_NEGATIVE_COLOR = '#ff1f8f';
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const interpolateHexColor = (startHex, endHex, position) => {
  const parseChannel = (hex, offset) => parseInt(hex.slice(offset, offset + 2), 16);
  const toHex = (channel) => Math.round(channel).toString(16).padStart(2, '0');
  const start = [1, 3, 5].map((offset) => parseChannel(startHex, offset));
  const end = [1, 3, 5].map((offset) => parseChannel(endHex, offset));

  return `#${start.map((channel, index) => (
    toHex(channel + ((end[index] - channel) * position))
  )).join('')}`;
};

const interpolateGradientColor = (colors, position) => {
  const scaledPosition = position * (colors.length - 1);
  const startIndex = Math.min(Math.floor(scaledPosition), colors.length - 2);
  const localPosition = scaledPosition - startIndex;
  return interpolateHexColor(colors[startIndex], colors[startIndex + 1], localPosition);
};

const hexToRgba = (hex, opacity) => {
  const channels = [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16));
  return `rgba(${channels.join(', ')}, ${opacity})`;
};

const getModulationValue = (value, average) => {
  const numericAverage = Number(average || 0);
  if (Math.abs(numericAverage) <= Number.EPSILON) {
    return null;
  }

  return Number((
    ((Number(value || 0) - numericAverage) / Math.abs(numericAverage)) * 100
  ).toFixed(2));
};

const formatModulationValue = (value) => {
  const numericValue = Number(value || 0);
  const prefix = numericValue > 0 ? '+' : '';
  return `${prefix}${numericValue.toFixed(1)}%`;
};

const WEEKDAYS = WEEKDAY_LABELS.map((label, index) => ({
  value: index + 1,
  label,
  color: interpolateGradientColor(
    [HOT_PINK, HOT_ORANGE, HOT_YELLOW],
    index / (WEEKDAY_LABELS.length - 1),
  ),
}));
const SITE_GRADIENT_COLORS = ['#00d6a3', '#00bff3', '#9b6bff'];
const AVERAGE_DARK_COLOR = '#aaa79d';
const AVERAGE_LIGHT_COLOR = '#929ca9';
const TREND_UP_COLOR = '#7cff6b';
const TREND_DOWN_COLOR = '#ff1744';
const TREND_FLAT_COLOR = '#40e0d0';
const TREND_UP_LIGHT_COLOR = '#00b83f';
const TREND_DOWN_LIGHT_COLOR = '#d90429';
const TREND_FLAT_LIGHT_COLOR = '#009eb3';
const EMPTY_LIST = [];

const parseLocalDate = (dateKey) => {
  const [year, month, day] = String(dateKey).split('-').map(Number);
  return new Date(year, month - 1, day);
};

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatShortDate = (dateKey) => parseLocalDate(dateKey).toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
});

const formatLongDate = (dateKey) => {
  const date = parseLocalDate(dateKey);
  const formattedDate = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  return `${formattedDate} (${weekday})`;
};

const formatFullDate = (dateKey) => parseLocalDate(dateKey).toLocaleDateString('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const formatMetricValue = (value, metric, compact = false) => {
  const options = compact
    ? {
      notation: 'compact',
      maximumFractionDigits: 1,
    }
    : {
      maximumFractionDigits: metric === 'revenue' ? 0 : 1,
    };

  if (metric === 'revenue') {
    return new Intl.NumberFormat('en-US', {
      ...options,
      style: 'currency',
      currency: 'USD',
    }).format(Number(value || 0));
  }

  return new Intl.NumberFormat('en-US', options).format(Number(value || 0));
};

const formatPeriod = (point, viewBy) => {
  if (viewBy === 'day') {
    return formatLongDate(point.period_start);
  }

  return `${formatShortDate(point.period_start)} – ${formatFullDate(point.period_end)}`;
};

function SegmentedFilter({
  label, options, value, onChange,
}) {
  return (
    <div className="dashboard-trend-filter">
      <span className="dashboard-trend-filter__label">{label}</span>
      <div className="dashboard-trend-segments" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`dashboard-trend-segment ${value === option.value ? 'is-active' : ''}`}
            aria-pressed={value === option.value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

SegmentedFilter.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  })).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func.isRequired,
};

function FilterChip({
  active, color, label, onClick,
}) {
  const activeColorStyle = active && color ? {
    backgroundImage: `linear-gradient(135deg, ${color}2b, ${color}12)`,
    borderColor: `${color}73`,
    boxShadow: `inset 0 0 0 1px ${color}14`,
  } : undefined;

  return (
    <button
      type="button"
      className={`dashboard-trend-chip ${active ? 'is-active' : ''}`}
      aria-pressed={active}
      style={activeColorStyle}
      onClick={onClick}
    >
      {color && <span className="dashboard-trend-chip__dot" style={{ backgroundColor: color }} />}
      {label}
    </button>
  );
}

FilterChip.defaultProps = {
  color: null,
};

FilterChip.propTypes = {
  active: PropTypes.bool.isRequired,
  color: PropTypes.string,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
};

function OrderTrendWidget({ date, t }) {
  const [isDarkMode, setIsDarkMode] = useState(() => (
    document.body.getAttribute('data-layout-mode') === 'dark'
  ));
  const [metric, setMetric] = useState('orders');
  const [weeks, setWeeks] = useState(10);
  const [viewBy, setViewBy] = useState('day');
  const [chartType, setChartType] = useState('columns');
  const [weekdays, setWeekdays] = useState([]);
  const [sites, setSites] = useState([]);
  const [availableSites, setAvailableSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendError, setTrendError] = useState(false);

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  useEffect(() => {
    const updateTheme = () => {
      setIsDarkMode(document.body.getAttribute('data-layout-mode') === 'dark');
    };
    const observer = new MutationObserver(updateTheme);

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-layout-mode'],
    });

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchSites = async () => {
      try {
        const response = await getOrderTrendSites();
        if (mounted) {
          setAvailableSites([...(response.sites || [])].sort((left, right) => (
            left.localeCompare(right, 'en', { sensitivity: 'base' })
          )));
        }
      } catch (error) {
        if (mounted) {
          toastr.error(t('Error fetching site filters'));
        }
      } finally {
        if (mounted) {
          setSitesLoading(false);
        }
      }
    };

    fetchSites();
    return () => {
      mounted = false;
    };
  }, [t]);

  useEffect(() => {
    let mounted = true;

    const fetchTrend = async () => {
      if (!date) {
        return;
      }

      setTrendLoading(true);
      setTrendError(false);
      setTrendData(null);

      try {
        const response = await getOrderTrend({
          isoDay: date,
          metric,
          weeks,
          viewBy,
          chartType,
          weekdays,
          sites,
        });

        if (mounted) {
          setTrendData(response);
        }
      } catch (error) {
        if (mounted) {
          setTrendError(true);
          toastr.error(t('Error fetching sales trend data'));
        }
      } finally {
        if (mounted) {
          setTrendLoading(false);
        }
      }
    };

    fetchTrend();
    return () => {
      mounted = false;
    };
  }, [chartType, date, metric, sites, t, viewBy, weekdays, weeks]);

  const weekOptions = useMemo(() => LOOKBACK_WEEKS.map((weekCount) => ({
    value: weekCount,
    label: `${weekCount} wk`,
  })), []);

  const points = trendData?.points || [];
  const siteMix = trendData?.site_mix || EMPTY_LIST;
  const dailyRows = trendData?.daily_rows || EMPTY_LIST;
  const todayKey = formatDateKey(new Date());
  const todayWeekday = ((new Date().getDay() + 6) % 7) + 1;
  const todayIsSelected = weekdays.length === 0 || weekdays.includes(todayWeekday);
  const partialPointIndex = points.findIndex((point) => (
    todayIsSelected && point.period_start <= todayKey && point.period_end >= todayKey
  ));
  const hasPartialPoint = partialPointIndex >= 0;
  const partialPoint = hasPartialPoint ? points[partialPointIndex] : null;
  const partialPointColor = viewBy === 'day' && partialPoint
    ? WEEKDAYS.find((weekday) => weekday.value === partialPoint.weekday)?.color || HOT_ORANGE
    : HOT_ORANGE;
  const averageColor = isDarkMode ? AVERAGE_DARK_COLOR : AVERAGE_LIGHT_COLOR;
  const weeklyGuideLabels = useMemo(() => {
    if (viewBy === 'week') {
      return points.map((point) => formatShortDate(point.period_start));
    }

    const seenWeeks = new Set();
    return points.reduce((labels, point) => {
      const pointDate = parseLocalDate(point.period_start);
      const dayOffset = (pointDate.getDay() + 6) % 7;
      pointDate.setDate(pointDate.getDate() - dayOffset);
      const weekKey = formatDateKey(pointDate);

      if (!seenWeeks.has(weekKey)) {
        seenWeeks.add(weekKey);
        labels.push(formatShortDate(point.period_start));
      }

      return labels;
    }, []);
  }, [points, viewBy]);
  const summary = useMemo(() => {
    const total = points.reduce((sum, point) => sum + Number(point.value || 0), 0);
    const peak = points.reduce((currentPeak, point) => (
      currentPeak === null || Number(point.value || 0) > Number(currentPeak.value || 0)
        ? point
        : currentPeak
    ), null);

    return {
      total,
      average: points.length > 0 ? total / points.length : 0,
      peak,
      daysShown: weeks * (weekdays.length || WEEKDAYS.length),
    };
  }, [points, weekdays.length, weeks]);

  const overallTrend = useMemo(() => {
    const selectedWeekdayValues = weekdays.length > 0
      ? weekdays
      : WEEKDAYS.map((weekday) => weekday.value);
    const chronologicalRows = [...dailyRows]
      .sort((left, right) => left.date.localeCompare(right.date));
    let trendSamples = chronologicalRows.map((row, index) => ({
      date: row.date,
      weekday: row.weekday,
      x: index,
      y: Number(row[metric] || 0),
    })).filter((sample) => sample.date !== todayKey);

    if (trendSamples.length > 0) {
      const finalWeekday = trendSamples[trendSamples.length - 1].weekday;
      let startingWeekday = finalWeekday;

      for (let offset = 1; offset <= 7; offset += 1) {
        const candidateWeekday = ((finalWeekday - 1 + offset) % 7) + 1;
        if (selectedWeekdayValues.includes(candidateWeekday)) {
          startingWeekday = candidateWeekday;
          break;
        }
      }

      const alignedStartIndex = trendSamples.findIndex((sample) => (
        sample.weekday === startingWeekday
      ));
      if (alignedStartIndex > 0) {
        trendSamples = trendSamples.slice(alignedStartIndex);
      }
    }

    if (trendSamples.length < 2) {
      return {
        color: TREND_FLAT_COLOR,
        direction: 'flat',
        percentage: 0,
        symbol: '→',
        values: points.map(() => null),
      };
    }

    const count = trendSamples.length;
    const totals = trendSamples.reduce((accumulator, sample) => ({
      x: accumulator.x + sample.x,
      y: accumulator.y + sample.y,
      xy: accumulator.xy + (sample.x * sample.y),
      xx: accumulator.xx + (sample.x * sample.x),
    }), {
      x: 0, y: 0, xy: 0, xx: 0,
    });
    const denominator = (count * totals.xx) - (totals.x * totals.x);
    const slope = denominator === 0
      ? 0
      : ((count * totals.xy) - (totals.x * totals.y)) / denominator;
    const intercept = (totals.y - (slope * totals.x)) / count;
    const firstTrendIndex = trendSamples[0].x;
    const lastTrendIndex = trendSamples[trendSamples.length - 1].x;
    const fittedStart = intercept + (slope * firstTrendIndex);
    const fittedEnd = intercept + (slope * lastTrendIndex);
    const percentage = Math.abs(fittedStart) > Number.EPSILON
      ? ((fittedEnd - fittedStart) / Math.abs(fittedStart)) * 100
      : 0;
    let direction = 'flat';
    let color = TREND_FLAT_COLOR;
    let symbol = '→';

    if (slope > 0) {
      direction = 'up';
      color = TREND_UP_COLOR;
      symbol = '↗';
    } else if (slope < 0) {
      direction = 'down';
      color = TREND_DOWN_COLOR;
      symbol = '↘';
    }

    let values;
    if (viewBy === 'day') {
      const rowIndexByDate = new Map(
        chronologicalRows.map((row, index) => [row.date, index]),
      );
      values = points.map((point) => {
        const rowIndex = rowIndexByDate.get(point.period_start);
        return rowIndex >= firstTrendIndex && rowIndex <= lastTrendIndex
          ? Number((intercept + (slope * rowIndex)).toFixed(2))
          : null;
      });
    } else {
      const completePointIndexes = points.reduce((indexes, point, index) => {
        const periodIncludesToday = point.period_start <= todayKey
          && point.period_end >= todayKey;
        if (!periodIncludesToday || !selectedWeekdayValues.includes(todayWeekday)) {
          indexes.push(index);
        }
        return indexes;
      }, []);
      const completePointPosition = new Map(
        completePointIndexes.map((pointIndex, index) => [pointIndex, index]),
      );
      const weeklyScale = selectedWeekdayValues.length;
      values = points.map((_, pointIndex) => {
        const position = completePointPosition.get(pointIndex);
        if (position === undefined) {
          return null;
        }

        const progress = completePointIndexes.length > 1
          ? position / (completePointIndexes.length - 1)
          : 0;
        return Number((
          (fittedStart + ((fittedEnd - fittedStart) * progress)) * weeklyScale
        ).toFixed(2));
      });
    }

    return {
      color,
      direction,
      percentage: Math.abs(percentage),
      symbol,
      values,
    };
  }, [dailyRows, metric, points, todayKey, todayWeekday, viewBy, weekdays]);
  let overallTrendColor = overallTrend.color;
  if (!isDarkMode) {
    if (overallTrend.direction === 'up') {
      overallTrendColor = TREND_UP_LIGHT_COLOR;
    } else if (overallTrend.direction === 'down') {
      overallTrendColor = TREND_DOWN_LIGHT_COLOR;
    } else {
      overallTrendColor = TREND_FLAT_LIGHT_COLOR;
    }
  }
  const trendDisplayColor = hexToRgba(overallTrendColor, isDarkMode ? 0.54 : 0.46);
  const modulationLimit = useMemo(() => {
    const largestVariance = points.reduce((largest, point) => {
      const variance = getModulationValue(point.value, point.average);
      return variance === null ? largest : Math.max(largest, Math.abs(variance));
    }, Math.max(10, overallTrend.percentage / 2));

    return Math.ceil(largestVariance / 10) * 10;
  }, [overallTrend.percentage, points]);
  const varianceGradientStops = useMemo(() => {
    if (points.length === 0) {
      return [];
    }

    const denominator = Math.max(points.length - 1, 1);
    const stops = points.reduce((gradientStops, point, index) => {
      if (index === partialPointIndex) {
        return gradientStops;
      }

      const variance = getModulationValue(point.value, point.average);
      if (variance === null) {
        return gradientStops;
      }
      const normalizedVariance = Math.max(-1, Math.min(1, variance / modulationLimit));

      gradientStops.push({
        offset: Number(((index / denominator) * 100).toFixed(2)),
        color: interpolateGradientColor(
          [VARIANCE_POSITIVE_COLOR, VARIANCE_NEUTRAL_COLOR, VARIANCE_NEGATIVE_COLOR],
          (1 - normalizedVariance) / 2,
        ),
        opacity: 1,
      });

      return gradientStops;
    }, []);

    if (stops.length === 0) {
      return [];
    }

    if (stops[0]?.offset !== 0) {
      stops.unshift({ ...stops[0], offset: 0 });
    }
    if (stops[stops.length - 1]?.offset !== 100) {
      stops.push({ ...stops[stops.length - 1], offset: 100 });
    }

    return stops;
  }, [modulationLimit, partialPointIndex, points]);

  const chartOptions = useMemo(() => {
    const isModulation = chartType === 'modulation';
    const mainFillType = chartType === 'columns' || isModulation ? 'gradient' : 'solid';
    const mainFillOpacity = 1;
    const averageFillOpacity = isModulation ? 0 : 0.1;
    let mainStrokeWidth = 0;
    if (chartType === 'line') {
      mainStrokeWidth = 3;
    } else if (isModulation) {
      mainStrokeWidth = 2;
    }
    let yAxisTitle = metric === 'revenue' ? t('Revenue') : t('Order volume');
    if (isModulation) {
      yAxisTitle = t('Variance from 8-week average');
    }
    const primaryColor = isModulation
      ? hexToRgba(averageColor, isDarkMode ? 0.62 : 0.52)
      : HOT_PINK;
    const seriesColors = hasPartialPoint
      ? [primaryColor, partialPointColor, averageColor, trendDisplayColor]
      : [primaryColor, averageColor, trendDisplayColor];
    const fillTypes = hasPartialPoint
      ? [mainFillType, 'solid', 'solid', 'solid']
      : [mainFillType, 'solid', 'solid'];
    const fillOpacities = hasPartialPoint
      ? [mainFillOpacity, 1, averageFillOpacity, 0]
      : [mainFillOpacity, averageFillOpacity, 0];
    const markerSizes = hasPartialPoint
      ? [chartType === 'line' ? 3 : 0, 5, 0, 0]
      : [chartType === 'line' ? 3 : 0, 0, 0];
    const strokeWidths = hasPartialPoint
      ? [mainStrokeWidth, 0, 1.25, 2]
      : [mainStrokeWidth, 1.25, 2];
    const dashArrays = hasPartialPoint ? [0, 0, 0, 3] : [0, 0, 3];
    let enabledTooltipSeries = [0, 1, 2];
    if (hasPartialPoint) {
      enabledTooltipSeries = chartType === 'columns' ? [0, 2, 3] : [0, 1, 2, 3];
    }

    return ({
      chart: {
        type: 'line',
        height: 380,
        stacked: false,
        fontFamily: 'inherit',
        foreColor: dashboardPalette.muted,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 420,
        },
      },
      colors: seriesColors,
      annotations: {
        xaxis: viewBy === 'day' ? weeklyGuideLabels.slice(1).map((label) => ({
          x: label,
          borderColor: isDarkMode ? 'rgba(224, 207, 151, 0.14)' : 'rgba(92, 106, 128, 0.13)',
          strokeDashArray: 3,
        })) : [],
      },
      dataLabels: {
        enabled: false,
      },
      fill: {
        type: fillTypes,
        opacity: fillOpacities,
        gradient: {
          type: isModulation ? 'horizontal' : 'vertical',
          shade: 'dark',
          shadeIntensity: isDarkMode ? 0.12 : 0.38,
          inverseColors: isDarkMode,
          opacityFrom: 1,
          opacityTo: isDarkMode ? 0.82 : 0.96,
          stops: [0, 100],
          colorStops: isModulation ? [varianceGradientStops] : [],
        },
      },
      grid: {
        borderColor: dashboardPalette.grid,
        strokeDashArray: 4,
        padding: {
          left: 8,
          right: 14,
          top: 6,
          bottom: 0,
        },
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right',
        fontWeight: 600,
        fontSize: '11px',
        labels: {
          colors: dashboardPalette.slate,
        },
        markers: {
          radius: 999,
        },
      },
      markers: {
        size: markerSizes,
        strokeWidth: 0,
        hover: {
          sizeOffset: 2,
        },
      },
      plotOptions: {
        bar: {
          columnWidth: points.length > 45 ? '62%' : '52%',
          borderRadius: 4,
          endingShape: 'rounded',
        },
      },
      stroke: {
        curve: 'straight',
        width: strokeWidths,
        dashArray: dashArrays,
        lineCap: 'round',
      },
      xaxis: {
        categories: points.map((point) => formatShortDate(point.period_start)),
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          rotate: -45,
          hideOverlappingLabels: true,
          trim: false,
          formatter: (value) => (
            viewBy === 'week' || weeklyGuideLabels.includes(value) ? value : ''
          ),
          style: {
            colors: points.map(() => dashboardPalette.muted),
            fontSize: '10px',
            fontWeight: 600,
          },
        },
      },
      yaxis: {
        min: isModulation ? -modulationLimit : 0,
        max: isModulation ? modulationLimit : undefined,
        forceNiceScale: true,
        title: {
          text: yAxisTitle,
          style: {
            color: dashboardPalette.muted,
            fontWeight: 600,
          },
        },
        labels: {
          formatter: (value) => (
            isModulation
              ? formatModulationValue(value)
              : formatMetricValue(value, metric, value >= 10000)
          ),
          style: {
            colors: [dashboardPalette.muted],
            fontSize: '10px',
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: 'light',
        enabledOnSeries: enabledTooltipSeries,
        x: {
          formatter: (value, { dataPointIndex }) => (
            points[dataPointIndex] ? formatPeriod(points[dataPointIndex], viewBy) : value
          ),
        },
        y: {
          formatter: (value) => (
            isModulation ? formatModulationValue(value) : formatMetricValue(value, metric)
          ),
        },
      },
    });
  }, [
    averageColor,
    chartType,
    hasPartialPoint,
    isDarkMode,
    metric,
    modulationLimit,
    partialPointColor,
    points,
    t,
    trendDisplayColor,
    varianceGradientStops,
    viewBy,
    weeklyGuideLabels,
  ]);

  const chartSeries = useMemo(() => {
    const isModulation = chartType === 'modulation';
    const metricLabel = metric === 'revenue' ? t('Revenue') : t('Orders');
    const modulationTrendIndexes = overallTrend.values.reduce((indexes, value, index) => {
      if (value !== null) {
        indexes.push(index);
      }
      return indexes;
    }, []);
    const modulationTrendPositions = new Map(
      modulationTrendIndexes.map((pointIndex, index) => [pointIndex, index]),
    );
    const signedTrendPercentage = overallTrend.direction === 'down'
      ? -overallTrend.percentage
      : overallTrend.percentage;
    let primarySeriesType = 'line';
    if (chartType === 'columns') {
      primarySeriesType = 'column';
    } else if (isModulation) {
      primarySeriesType = 'area';
    }
    const getModulationTrendValue = (pointIndex) => {
      const position = modulationTrendPositions.get(pointIndex);
      if (position === undefined) {
        return null;
      }
      const progress = modulationTrendIndexes.length > 1
        ? position / (modulationTrendIndexes.length - 1)
        : 0.5;
      return Number((
        (-signedTrendPercentage / 2) + (signedTrendPercentage * progress)
      ).toFixed(2));
    };
    const series = [{
      name: isModulation ? `${metricLabel} ${t('variance')}` : metricLabel,
      type: primarySeriesType,
      data: points.map((point, index) => {
        let fillColor = viewBy === 'day'
          ? WEEKDAYS.find((weekday) => weekday.value === point.weekday)?.color
          : HOT_PINK;
        let y = isModulation
          ? getModulationValue(point.value, point.average)
          : Number(point.value || 0);

        if (index === partialPointIndex && chartType === 'columns') {
          fillColor = hexToRgba(partialPointColor, 0.42);
        }
        if (chartType !== 'columns' && index === partialPointIndex) {
          y = null;
        }

        return {
          x: formatShortDate(point.period_start),
          y,
          fillColor,
        };
      }),
    }];

    if (hasPartialPoint) {
      series.push({
        name: viewBy === 'day' ? t('Today · partial') : t('Current period · partial'),
        type: 'line',
        data: points.map((point, index) => {
          let y = null;
          if (index === partialPointIndex) {
            y = isModulation
              ? getModulationValue(point.value, point.average)
              : Number(point.value || 0);
          }

          return {
            x: formatShortDate(point.period_start),
            y,
            fillColor: partialPointColor,
          };
        }),
      });
    }

    series.push({
      name: isModulation
        ? t('Previous 8-week average baseline')
        : t('Previous 8-week average'),
      type: isModulation ? 'line' : 'area',
      data: points.map((point) => ({
        x: formatShortDate(point.period_start),
        y: isModulation ? 0 : Number(point.average || 0),
      })),
    });
    series.push({
      name: `${t('Overall trend')} ${overallTrend.symbol} ${overallTrend.percentage.toFixed(1)}%`,
      type: 'line',
      data: points.map((point, index) => ({
        x: formatShortDate(point.period_start),
        y: isModulation
          ? getModulationTrendValue(index)
          : overallTrend.values[index],
      })),
    });

    return series;
  }, [
    chartType,
    hasPartialPoint,
    metric,
    overallTrend,
    partialPointColor,
    partialPointIndex,
    points,
    t,
    viewBy,
  ]);

  const siteMixOptions = useMemo(() => {
    const siteMixTotal = siteMix.reduce((sum, site) => sum + Number(site.value || 0), 0);

    return {
      chart: {
        type: 'bar',
        fontFamily: 'inherit',
        foreColor: dashboardPalette.muted,
        toolbar: {
          show: false,
        },
        animations: {
          enabled: true,
          easing: 'easeinout',
          speed: 420,
        },
      },
      colors: siteMix.map((_, index) => interpolateGradientColor(
        SITE_GRADIENT_COLORS,
        siteMix.length === 1 ? 0.5 : index / (siteMix.length - 1),
      )),
      dataLabels: {
        enabled: true,
        textAnchor: 'start',
        offsetX: 8,
        style: {
          colors: [dashboardPalette.slate],
          fontSize: '10px',
          fontWeight: 700,
        },
        background: {
          enabled: false,
        },
        formatter: (value) => {
          const percentage = siteMixTotal > 0 ? (Number(value || 0) / siteMixTotal) * 100 : 0;
          return `${formatMetricValue(value, metric)} · ${percentage.toFixed(1)}%`;
        },
      },
      grid: {
        borderColor: dashboardPalette.grid,
        strokeDashArray: 4,
        padding: {
          left: 10,
          right: 90,
          top: 2,
          bottom: 0,
        },
      },
      legend: {
        show: false,
      },
      fill: {
        type: 'gradient',
        gradient: {
          type: 'horizontal',
          shade: 'dark',
          shadeIntensity: isDarkMode ? 0.12 : 0.38,
          inverseColors: isDarkMode,
          opacityFrom: 1,
          opacityTo: isDarkMode ? 0.82 : 0.96,
          stops: [0, 100],
        },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          barHeight: siteMix.length > 6 ? '58%' : '38%',
          borderRadius: 4,
          endingShape: 'rounded',
          dataLabels: {
            position: 'top',
          },
        },
      },
      tooltip: {
        theme: 'light',
        y: {
          formatter: (value) => formatMetricValue(value, metric),
          title: {
            formatter: () => '',
          },
        },
      },
      xaxis: {
        categories: siteMix.map((site) => site.site),
        labels: {
          formatter: (value) => formatMetricValue(value, metric, Number(value) >= 10000),
          style: {
            colors: dashboardPalette.muted,
            fontSize: '10px',
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        labels: {
          maxWidth: 170,
          style: {
            colors: siteMix.map(() => dashboardPalette.slate),
            fontSize: '10px',
            fontWeight: 700,
          },
        },
      },
    };
  }, [isDarkMode, metric, siteMix]);

  const siteMixSeries = useMemo(() => ([{
    name: metric === 'revenue' ? t('Revenue') : t('Orders'),
    data: siteMix.map((site) => Number(site.value || 0)),
  }]), [metric, siteMix, t]);

  const siteMixChartHeight = Math.max(220, Math.min(450, (siteMix.length * 44) + 75));

  const toggleValue = (selectedValues, setSelectedValues, selectedValue) => {
    if (selectedValues.includes(selectedValue)) {
      setSelectedValues(selectedValues.filter((value) => value !== selectedValue));
      return;
    }

    setSelectedValues([...selectedValues, selectedValue]);
  };

  return (
    <Card className="dashboard-panel dashboard-trend-widget mt-3">
      <CardBody className="dashboard-panel__body dashboard-trend-widget__body">
        <div className="dashboard-panel__header dashboard-trend-widget__header">
          <div>
            <span className="dashboard-panel__eyebrow">{t('Sales trends')}</span>
            <h5 className="dashboard-panel__title">
              {metric === 'revenue' ? t('Revenue by weekday') : t('Orders by weekday')}
            </h5>
          </div>
          <span className="dashboard-panel__hint">{t('Compared with the previous 8-week average')}</span>
        </div>

        <div className="dashboard-trend-primary-filters">
          <SegmentedFilter
            label={t('Metric')}
            options={METRICS.map((option) => ({ ...option, label: t(option.label) }))}
            value={metric}
            onChange={setMetric}
          />
          <SegmentedFilter
            label={t('Look back')}
            options={weekOptions}
            value={weeks}
            onChange={setWeeks}
          />
          <SegmentedFilter
            label={t('View by')}
            options={VIEW_OPTIONS.map((option) => ({ ...option, label: t(option.label) }))}
            value={viewBy}
            onChange={setViewBy}
          />
          <SegmentedFilter
            label={t('Chart style')}
            options={CHART_TYPES.map((option) => ({ ...option, label: t(option.label) }))}
            value={chartType}
            onChange={setChartType}
          />
        </div>

        <div className="dashboard-trend-secondary-filters">
          <div className="dashboard-trend-filter">
            <span className="dashboard-trend-filter__label">{t('Days')}</span>
            <div className="dashboard-trend-chips" role="group" aria-label={t('Days')}>
              <FilterChip
                active={weekdays.length === 0}
                label={t('All days')}
                onClick={() => setWeekdays([])}
              />
              {WEEKDAYS.map((weekday) => (
                <FilterChip
                  key={weekday.value}
                  active={weekdays.length === 0 || weekdays.includes(weekday.value)}
                  color={weekday.color}
                  label={t(weekday.label)}
                  onClick={() => toggleValue(weekdays, setWeekdays, weekday.value)}
                />
              ))}
            </div>
          </div>

          <div className="dashboard-trend-filter">
            <span className="dashboard-trend-filter__label">{t('Sites')}</span>
            <div className="dashboard-trend-chips" role="group" aria-label={t('Sites')}>
              <FilterChip
                active={sites.length === 0}
                label={t('All sites')}
                onClick={() => setSites([])}
              />
              {sitesLoading && (
                <span className="dashboard-trend-loading">
                  <span className="mdi mdi-loading mdi-spin" aria-hidden="true" />
                  {t('Loading sites')}
                </span>
              )}
              {!sitesLoading && availableSites.map((site) => (
                <FilterChip
                  key={site}
                  active={sites.length === 0 || sites.includes(site)}
                  label={site}
                  onClick={() => toggleValue(sites, setSites, site)}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="dashboard-trend-summary" aria-live="polite">
          <div className="dashboard-trend-summary__card">
            <span className="dashboard-trend-summary__label">
              {metric === 'revenue' ? t('Total revenue') : t('Total orders')}
            </span>
            <strong className="dashboard-trend-summary__value">
              {trendLoading ? '—' : formatMetricValue(summary.total, metric, true)}
            </strong>
          </div>
          <div className="dashboard-trend-summary__card">
            <span className="dashboard-trend-summary__label">
              {viewBy === 'day' ? t('Average / day') : t('Average / week')}
            </span>
            <strong className="dashboard-trend-summary__value">
              {trendLoading ? '—' : formatMetricValue(summary.average, metric)}
            </strong>
          </div>
          <div className="dashboard-trend-summary__card">
            <span className="dashboard-trend-summary__label">
              {viewBy === 'day' ? t('Peak day') : t('Peak week')}
            </span>
            <strong className="dashboard-trend-summary__value">
              {trendLoading || summary.peak === null
                ? '—'
                : formatMetricValue(summary.peak.value, metric)}
            </strong>
            {!trendLoading && summary.peak !== null && (
              <span className="dashboard-trend-summary__detail">
                {formatPeriod(summary.peak, viewBy)}
              </span>
            )}
          </div>
          <div className="dashboard-trend-summary__card">
            <span className="dashboard-trend-summary__label">{t('Days shown')}</span>
            <strong className="dashboard-trend-summary__value">
              {trendLoading ? '—' : summary.daysShown}
            </strong>
          </div>
        </div>

        <div className="dashboard-trend-chart">
          {trendLoading && (
            <div className="dashboard-trend-chart__state">
              <span className="mdi mdi-loading mdi-spin fs-2" aria-hidden="true" />
              <span>{t('Loading trend')}</span>
            </div>
          )}
          {!trendLoading && trendError && (
            <div className="dashboard-trend-chart__state is-error">
              <span className="mdi mdi-alert-circle-outline fs-2" aria-hidden="true" />
              <span>{t('Unable to load trend data.')}</span>
            </div>
          )}
          {!trendLoading && !trendError && points.length === 0 && (
            <div className="dashboard-trend-chart__state">
              <span className="mdi mdi-chart-bar fs-2" aria-hidden="true" />
              <span>{t('No sales found for these filters.')}</span>
            </div>
          )}
          {!trendLoading && !trendError && points.length > 0 && (
            <ReactApexChart
              options={chartOptions}
              series={chartSeries}
              type="line"
              height={380}
            />
          )}
        </div>

        {!trendLoading && !trendError && (
          <div className="dashboard-trend-detail">
            <section className="dashboard-trend-detail__panel" aria-labelledby="site-mix-title">
              <div className="dashboard-trend-detail__header">
                <h6 id="site-mix-title" className="dashboard-trend-detail__title">
                  {t('Site mix — current filter')}
                </h6>
                <span className="dashboard-trend-detail__hint">
                  {`${siteMix.length} ${siteMix.length === 1 ? t('site') : t('sites')}`}
                </span>
              </div>
              {siteMix.length > 0 ? (
                <ReactApexChart
                  options={siteMixOptions}
                  series={siteMixSeries}
                  type="bar"
                  height={siteMixChartHeight}
                />
              ) : (
                <div className="dashboard-trend-detail__empty">
                  {t('No site data found for these filters.')}
                </div>
              )}
            </section>

            <section className="dashboard-trend-detail__panel" aria-labelledby="trend-data-title">
              <div className="dashboard-trend-detail__header">
                <h6 id="trend-data-title" className="dashboard-trend-detail__title">
                  {t('Data')}
                </h6>
                <span className="dashboard-trend-detail__hint">
                  {`${dailyRows.length} ${t('days')} · ${t('sorted newest first')}`}
                </span>
              </div>
              <div className="dashboard-trend-table-wrap">
                <table className="dashboard-trend-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('Date')}</th>
                      <th scope="col">{t('Day')}</th>
                      <th scope="col" className="is-numeric">{t('Orders')}</th>
                      <th scope="col" className="is-numeric">{t('Revenue')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyRows.map((row) => (
                      <tr key={row.date}>
                        <th scope="row">{formatFullDate(row.date)}</th>
                        <td>
                          {WEEKDAYS.find((weekday) => weekday.value === row.weekday)?.label || '—'}
                        </td>
                        <td className="is-numeric">{formatMetricValue(row.orders, 'orders')}</td>
                        <td className="is-numeric">{formatMetricValue(row.revenue, 'revenue')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

OrderTrendWidget.propTypes = {
  date: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(OrderTrendWidget);
